import axios, { AxiosError, Canceler } from 'axios'
import { Component, Model, Prop, Ref, Vue } from 'vue-property-decorator'
import { Cropper } from 'vue-advanced-cropper'

import {
  CropperImage,
  CropperImageCoordinates,
  CropperImageProp,
  CropperInstance,
  CropperVisibleArea,
} from '@/types/cropper'

import {
  Image as IImage,
  ImageTransform,
  ImageTransformModelId,
  SizeImage,
} from '@/types/image'
import nestedFreeze from '@/helpers/nestedFreeze'
import mergeStrings from '@/helpers/mergeStrings'
import cloneObject from '@/helpers/cloneObject'
import messages from '@/config/messages'
import isEmpty from '@/helpers/isEmpty'

import ImageCropperPreview from './ImageCropperPreview/ImageCropperPreview.vue'
import CustomBackgroundWrapper from './CustomBackgroundWrapper/CustomBackgroundWrapper.vue'
import SiriusAPI from '@/api'

type CopyrightImage = NonNullable<IImage['copyrightImage']>
type CropCoordinates = Pick<
  ImageTransform,
  'crop_h' | 'crop_w' | 'crop_x' | 'crop_y'
>

const createEmptyCopyrightImage = (): CopyrightImage => ({
  url: '',
  source_url: '',
  source: '',
  copyright_file: null,
})

const createEmptyImage = (): IImage => ({
  url: '',
  id: 0,
  copyrightImage: createEmptyCopyrightImage(),
})

const hasChangedValues = (pairs: [unknown, unknown][]) =>
  pairs.some(([oldValue, newValue]) => oldValue !== newValue)

@Component({
  components: {
    Cropper,
    CustomBackgroundWrapper,
    ImageCropperPreview,
  },
})
export default class ImageCropper extends Vue {
  @Model('change', { type: Number, default: -1 }) private value!: number
  @Prop({ type: Number }) private readonly containerAspectRatio?: number
  @Prop({ type: Array }) private readonly acceptFiles?: string[]
  @Prop({ type: Boolean, default: false }) private readonly loading!: boolean
  @Prop({ type: Number })
  private readonly imageTransformModelId?: ImageTransformModelId
  @Prop({ type: Number })
  private readonly imageTransformItemId!: number
  @Prop({ type: Boolean, default: false })
  private readonly selectedPreview!: boolean
  @Prop({ type: String, default: 'blue-grey lighten-5' })
  private readonly backgroundColor!: string
  @Prop({ type: String, default: 'default' }) private readonly place!:
    | 'default'
    | 'detailImage'
  @Prop({ type: Boolean, default: false })
  private readonly hiddenControls!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly hiddenButtons!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly noGutters!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly readonlyCropper!: boolean
  @Prop({ type: String, default: '' })
  private readonly titleOneAllImages!: string
  @Prop({ type: Array, required: true })
  private readonly images!: CropperImageProp[]

  @Prop({ type: String, default: '' })
  private readonly title!: string
  @Prop({ type: String, default: '' })
  private readonly infoText!: string
  @Prop({ type: Number }) private maxSize?: number
  @Prop({ type: Boolean, default: true }) switchEnabled!: boolean
  @Prop({ type: Boolean, default: true }) buttonsEnabled!: boolean
  @Prop({ type: Boolean, default: true }) isActiveCropper!: boolean
  @Prop({ type: Boolean, default: undefined }) oneImageForAllFormats?: boolean
  @Prop({ type: Boolean, default: false })
  private readonly hidePreviews!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly isShowCloseButton!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly enabledSource!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly sourceNonRequired!: boolean

  @Ref('cropper') private readonly cropperRef?: CropperInstance
  @Ref('imageSource') private readonly imageSourceRef?: {
    resetForm: () => void
    isShowConfirmForm: boolean
  }

  private cropperKey = 1
  private isLoading = false
  private isReadyCropper = false
  private stencilAspectRatio = 0
  private currentOriginalImageUrl = ''
  private objectURLImages: string[] = []
  private currentImageIndex = -1
  private localOneImageForAllFormats = false
  private currentZoom = 0
  private initialImages: CropperImageProp[] = []
  private localImages: CropperImageProp[] = []
  private alwaysSelectedPreview = false
  private queueSaveRequestKeys: Record<string, Canceler | boolean> = {}
  private errorSaveRequestKeys: string[] = []
  private successSaveRequestKeys: string[] = []
  private forceImageSourceKey = Date.now()
  private forceRecreateCropperKey = 1

  private imageSize = {
    width: 666,
    height: 444,
  }

  private visibleArea = {
    width: 666,
    height: 444,
  }

  private get zoomStep() {
    return 0.01
  }

  private get localIsLoading() {
    return this.loading || this.isLoading
  }

  private get imageContainerAspectRatio() {
    if (this.place === 'detailImage') {
      return 1.5
    }

    if (this.containerAspectRatio) {
      return this.containerAspectRatio
    }

    return this.visibleArea.width / this.visibleArea.height
  }

  private get changedSourceImageTransforms() {
    if (!this.enabledSource) {
      return
    }

    const oldImages = this.initialImages
    const newImages = this.localImages

    const map = new Map<string, CropperImageProp[]>()

    newImages.forEach((newImage, newImageIndex) => {
      if (!newImage.transform.image?.url) {
        return
      }

      const oldImage = oldImages[newImageIndex]
      const oldCopyright = oldImage?.transform.image?.copyrightImage
      const newCopyright = newImage.transform.image?.copyrightImage

      const hasChanges = hasChangedValues([
        [oldCopyright?.source || null, newCopyright?.source || null],
        [oldCopyright?.source_url || null, newCopyright?.source_url || null],
        [oldCopyright?.url || null, newCopyright?.url || null],
        [
          oldCopyright?.copyright_file || null,
          newCopyright?.copyright_file || null,
        ],
        [
          oldImage?.transform.image?.id || null,
          newImage.transform.image?.id || null,
        ],
      ])

      if (!hasChanges && !this.sourceNonRequired) {
        return
      }

      const imageItem = map.get(newImage.transform.image?.url)

      if (imageItem) {
        imageItem.push(newImage)
      } else {
        map.set(newImage.transform.image?.url, [newImage])
      }
    })

    return map
  }

  private get hasChanges() {
    const oldImages = this.initialImages
    const newImages = this.localImages

    if (this.enabledSource && this.changedSourceImageTransforms?.size) {
      return true
    }

    if (newImages.length === 0 || oldImages.length === 0) {
      return false
    }

    const errorSaveRequestKeys = this.errorSaveRequestKeys

    return newImages.some((newImage, index) => {
      const oldImage = oldImages[index]

      return (
        errorSaveRequestKeys.includes(oldImage.key) ||
        this.hasChangesInImageTransform(oldImage, newImage)
      )
    })
  }

  private get currentImage() {
    return (
      this.localImages[this.currentImageIndex] ||
      this.localImages.find(({ transform: { image_id } }) => !!image_id) ||
      this.localImages[0] ||
      null
    )
  }

  private get isSaving() {
    return !isEmpty(this.queueSaveRequestKeys)
  }

  private get isDynamicRatio() {
    return !this.currentImage?.aspectRatio
  }

  private get minWidthImage() {
    if (!this.localOneImageForAllFormats) {
      return this.currentImage?.width
    }

    return Math.max(0, ...this.localImages.map(({ width }) => width || 0))
  }

  private get minHeightImage() {
    if (!this.localOneImageForAllFormats) {
      return this.currentImage?.height
    }

    return Math.max(0, ...this.localImages.map(({ height }) => height || 0))
  }

  private get currentAspectRatio() {
    if (!this.currentImage) return

    if (this.place === 'detailImage' && this.currentImageIndex === -1) {
      return this.imageContainerAspectRatio
    }

    return (
      this.currentImage?.aspectRatio ??
      this.imageSize.width / this.imageSize.height
    )
  }

  private get canSaveImage() {
    return !!this.currentImage?.transform.image?.url
  }

  private get imageSourceKey() {
    return `${this.forceImageSourceKey}_${this.currentImageIndex}`
  }

  private validateImages() {
    if (!this.enabledSource) {
      return ''
    }

    let errorMessage = ''

    const imagesWithPhoto = this.localImages.filter(
      ({ transform }) => !!transform.image?.url
    )

    for (const image of imagesWithPhoto) {
      if (
        this.sourceNonRequired &&
        this.currentImage?.transform.image?.url === image.transform.image?.url
      ) {
        if (!this.imageSourceRef || !this.imageSourceRef.isShowConfirmForm) {
          continue
        }
      }

      const copyrightImage = image.transform.image?.copyrightImage
      let isValidImageSource = !!copyrightImage?.source

      if (this.sourceNonRequired && !isValidImageSource) {
        continue
      }

      if (copyrightImage?.source_url) {
        isValidImageSource = !!(
          copyrightImage.source &&
          (copyrightImage.copyright_file || copyrightImage.url)
        )
      }

      if (!isValidImageSource) {
        errorMessage = `Необходимо заполнить поля, относящиеся к источнику фото для кропа «${
          image.description || image.title
        }»`

        break
      }
    }

    return errorMessage
  }

  private hasChangesInImageTransform(
    oldCropImage: CropperImageProp,
    newCropImage: CropperImageProp
  ) {
    const {
      crop_h: oldCropH,
      crop_w: oldCropW,
      crop_x: oldCropX,
      crop_y: oldCropY,
      image_id: oldImageId,
    } = oldCropImage.transform

    const {
      crop_h: newCropH,
      crop_w: newCropW,
      crop_x: newCropX,
      crop_y: newCropY,
      image_id: newImageId,
    } = newCropImage.transform

    return (
      oldImageId !== newImageId ||
      [
        [oldCropH, newCropH],
        [oldCropW, newCropW],
        [oldCropX, newCropX],
        [oldCropY, newCropY],
      ].some(([oldVal, newVal]) => {
        oldVal = oldVal || 0
        newVal = newVal || 0

        return Math.abs(oldVal - newVal) >= 2
      })
    )
  }

  private stencilSize({
    boundaries,
  }: {
    boundaries: { width: number; height: number }
  }) {
    if (this.isDynamicRatio && this.cropperRef) {
      const {
        imageSize: { width: imageWidth, height: imageHeight },
      } = this.cropperRef
      const aspectRatio = imageWidth / imageHeight
      const width = Math.floor(boundaries.height * aspectRatio)

      return {
        width,
        height: boundaries.height,
      }
    }

    return {
      width: boundaries.width,
      height: boundaries.height,
    }
  }

  private defaultBoundaries({ cropper }: { cropper: HTMLDivElement }) {
    return {
      width: cropper.clientWidth,
      height: cropper.clientHeight,
    }
  }

  private defaultSize() {
    if (!this.currentImage || !this.cropperRef) return

    const { imageSize } = this.cropperRef

    if (this.isDynamicRatio) {
      let width = imageSize.width
      let height = imageSize.height

      if (imageSize.width > imageSize.height) {
        width = this.currentImage.transform.crop_w || 0
        height =
          (this.currentImage.transform.crop_w || 0) *
          (imageSize.width / imageSize.height)
      } else {
        height = this.currentImage.transform.crop_h || 0
        width =
          (this.currentImage.transform.crop_h || 0) *
          (imageSize.height / imageSize.width)
      }

      width = Math.round(Math.min(width, imageSize.width))
      height = Math.round(Math.min(height, imageSize.height))

      return {
        width,
        height,
      }
    }

    const { crop_h: cropHeight, crop_w: cropWidth } =
      this.calcDefaultCoordinates({
        aspectRatio: this.currentAspectRatio,
        imgWidth: imageSize.width,
        imgHeight: imageSize.height,
      })

    if (this.currentImageIndex === -1) {
      return {
        width: imageSize.width,
        height: imageSize.height,
      }
    }

    return {
      width: this.currentImage.transform.crop_w || cropWidth,
      height: this.currentImage.transform.crop_h || cropHeight,
    }
  }

  private defaultPosition() {
    if (!this.currentImage || !this.cropperRef) return

    const { boundaries, imageSize } = this.cropperRef

    if (this.isDynamicRatio) {
      const cropX = this.currentImage.transform.crop_x || 0
      const cropY = this.currentImage.transform.crop_y || 0

      const visibleArea = {
        width: 0,
        height: 0,
      }

      let cropWidth = 0
      let cropHeight = 0
      let left = this.currentImage.transform.crop_x || 0
      let top = this.currentImage.transform.crop_y || 0

      if (imageSize.width > imageSize.height) {
        cropHeight =
          (this.currentImage.transform.crop_w || 0) *
          (imageSize.height / imageSize.width)
        visibleArea.width = this.currentImage.transform.crop_w || 0
        visibleArea.height =
          visibleArea.width * (boundaries.height / boundaries.width)

        top = Math.round(
          cropY > 0
            ? (visibleArea.height - cropHeight) / 2 + cropY
            : (imageSize.height - cropHeight) / 2
        )
      } else {
        cropWidth =
          (this.currentImage.transform.crop_h || 0) *
          (imageSize.width / imageSize.height)
        visibleArea.height = this.currentImage.transform.crop_h || 0
        visibleArea.width =
          visibleArea.height * (boundaries.width / boundaries.height)

        left = Math.round(
          cropX > 0
            ? (visibleArea.width - cropWidth) / 2 + cropX
            : (imageSize.width - cropWidth) / 2
        )
      }

      return {
        left: Math.max(left, 0),
        top: Math.max(top, 0),
      }
    }

    const { crop_x: left, crop_y: top } = this.calcDefaultCoordinates({
      aspectRatio: this.currentAspectRatio,
      imgWidth: imageSize.width,
      imgHeight: imageSize.height,
    })

    if (this.currentImageIndex === -1) {
      return {
        top,
        left,
      }
    }

    return {
      top: this.currentImage.transform.crop_y ?? top,
      left: this.currentImage.transform.crop_x ?? left,
    }
  }

  private calcDefaultCoordinates({
    aspectRatio,
    imgHeight,
    imgWidth,
    zoom = 0,
    offsetLeft = 0.5,
    offsetTop = 0.5,
  }: {
    aspectRatio: number | undefined
    imgHeight: number
    imgWidth: number
    zoom?: number
    offsetLeft?: number
    offsetTop?: number
  }): CropCoordinates {
    if (!aspectRatio) {
      return { crop_x: 0, crop_y: 0, crop_w: imgWidth, crop_h: imgHeight }
    }

    let width = Math.min(Math.floor(imgHeight * aspectRatio), imgWidth)
    let height = Math.min(Math.floor(imgWidth / aspectRatio), imgHeight)

    width -= Math.round(width * zoom)
    height -= Math.round(height * zoom)

    const top = Math.floor((imgHeight - height) * offsetTop)
    const left = Math.floor((imgWidth - width) * offsetLeft)

    return {
      crop_x: left,
      crop_y: top,
      crop_w: width,
      crop_h: height,
    }
  }

  private toggleOneImageForAllFormats(val = !this.localOneImageForAllFormats) {
    this.localOneImageForAllFormats = this.oneImageForAllFormats ?? val
  }

  private onClickPreview(e: Event, index: number) {
    const target = e.target as HTMLElement | null

    if (target?.closest('.v-btn')) return

    const isImageForAllFormats = this.localImages.every(
      ({ transform: { image } }) =>
        image?.url === this.localImages[0].transform.image?.url
    )

    const currentImageIndex =
      (!this.alwaysSelectedPreview || isImageForAllFormats) &&
      this.currentImageIndex === index
        ? -1
        : index

    if (currentImageIndex !== this.currentImageIndex) {
      this.currentZoom = 0
      this.currentImageIndex = currentImageIndex
    }
  }

  private onCropPreview(index: number) {
    this.currentImageIndex = index
  }

  private resetImageTransform(localImage: CropperImageProp) {
    localImage.transform.crop_y = null
    localImage.transform.crop_x = null
    localImage.transform.crop_w = null
    localImage.transform.crop_h = null
    localImage.transform.image_id = null
    localImage.transform.settings = null
    localImage.transform.image = createEmptyImage()
  }

  private applyImageTransform(
    localImage: CropperImageProp,
    image: IImage,
    coordinates: CropCoordinates
  ) {
    localImage.transform.image = image
    localImage.transform.image_id = image.id
    localImage.transform.crop_h = coordinates.crop_h
    localImage.transform.crop_w = coordinates.crop_w
    localImage.transform.crop_x = coordinates.crop_x
    localImage.transform.crop_y = coordinates.crop_y
  }

  private getDefaultImageIndex() {
    const initialAspectRatio =
      this.containerAspectRatio ?? this.imageContainerAspectRatio

    const defaultImageIndex = this.localImages.findIndex(
      (image) => image.aspectRatio === initialAspectRatio
    )

    return Math.max(defaultImageIndex, 0)
  }

  private onReplacePreview(index: number) {
    this.currentImageIndex = index

    if (index === -1) {
      this.localImages.forEach((localImage) => {
        this.resetImageTransform(localImage)
      })
    } else {
      this.resetImageTransform(this.localImages[index])
      this.toggleOneImageForAllFormats(false)
    }

    this.imageSourceRef?.resetForm()
    this.currentZoom = 0
  }

  private onCancel() {
    if (this.currentImageIndex === -1) {
      this.currentImageIndex =
        this.value > -1 ? this.value : this.getDefaultImageIndex()
    }

    this.$emit('cancel')
    this.initImages()
    this.forceRecreateCropperKey++
  }

  private saveImage(localImage: CropperImageProp) {
    const {
      key: cropImageKey,
      transform: {
        crop_h: cropH,
        crop_w: cropW,
        crop_x: cropX,
        crop_y: cropY,
        image_id: imageId,
        id,
      },
    } = localImage

    const canceler = this.queueSaveRequestKeys[cropImageKey]

    if (typeof canceler === 'boolean') {
      return
    }

    if (typeof canceler === 'function') {
      canceler()
    }

    this.errorSaveRequestKeys = this.errorSaveRequestKeys.filter(
      (key) => key !== cropImageKey
    )

    this.$set(this.queueSaveRequestKeys, cropImageKey, true)

    if (!imageId) {
      this.$nextTick(() => {
        this.$delete(this.queueSaveRequestKeys, cropImageKey)
      })

      return
    }

    const cancelTokenSource = axios.CancelToken.source()

    if (id) {
      this.$set(
        this.queueSaveRequestKeys,
        cropImageKey,
        cancelTokenSource.cancel
      )
    }

    const requestPromise = id
      ? SiriusAPI.image.changeImageTransform({
          id,
          crop_h: cropH,
          crop_w: cropW,
          crop_x: cropX,
          crop_y: cropY,
          image_id: imageId,
          model_id: this.imageTransformModelId,
          item_id: this.imageTransformItemId,
          responseStatusOnly: 1,
          cancelToken: cancelTokenSource.token,
          isShowNotify: false,
        })
      : SiriusAPI.image
          .addImageTransform({
            crop_h: cropH,
            crop_w: cropW,
            crop_x: cropX,
            crop_y: cropY,
            image_id: imageId,
            model_id: this.imageTransformModelId,
            item_id: this.imageTransformItemId,
            isShowNotify: false,
          })
          .then(({ item: { id: imageTransformId } }) => {
            localImage.transform.id = imageTransformId
          })

    requestPromise
      .then(() => {
        this.$delete(this.queueSaveRequestKeys, cropImageKey)

        const hasChanges = hasChangedValues([
          [cropH, localImage.transform.crop_h],
          [cropW, localImage.transform.crop_w],
          [cropX, localImage.transform.crop_x],
          [cropY, localImage.transform.crop_y],
          [imageId, localImage.transform.image_id],
        ])

        if (hasChanges) {
          this.saveImage(localImage)
        } else {
          this.successSaveRequestKeys.push(cropImageKey)
          this.notification()
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          return
        }

        this.$delete(this.queueSaveRequestKeys, cropImageKey)
        this.errorSaveRequestKeys.push(cropImageKey)

        this.notification()
      })
      .finally(() => {
        if (!this.isSaving) {
          this.successSaveRequestKeys = []

          if (this.errorSaveRequestKeys.length) {
            this.$emit('save:error')
          }
        }
      })
  }

  private formatCropDescriptions(keys: Set<string>) {
    const descriptionParts: string[] = []

    keys.forEach((key) => {
      const description = this.localImages.find(
        (image) => image.key === key
      )?.description

      if (description) {
        descriptionParts.push(`«${description}»`)
      }
    })

    return descriptionParts.join(', ')
  }

  private notification() {
    if (this.isSaving) {
      return
    }

    const successSaveRequestKeys = new Set(this.successSaveRequestKeys)
    const errorSaveRequestKeys = new Set(this.errorSaveRequestKeys)

    if (successSaveRequestKeys.size) {
      const cropDescriptions = this.formatCropDescriptions(
        successSaveRequestKeys
      )

      const title = mergeStrings({
        values: [
          successSaveRequestKeys.size > 1 ? 'Кропы' : 'Кроп',
          cropDescriptions,
          successSaveRequestKeys.size > 1 ? 'сохранены' : 'сохранен',
        ],
      })

      this.$notify({
        title,
        type: 'success',
        autoCloseTime: 7e3,
      })
    }

    if (errorSaveRequestKeys.size) {
      const cropDescriptions = this.formatCropDescriptions(errorSaveRequestKeys)

      const title = mergeStrings({
        values: [
          errorSaveRequestKeys.size > 1
            ? 'Ошибка сохранения кропов'
            : 'Ошибка сохранения кропа',
          cropDescriptions,
        ],
      })

      this.$notify({
        type: 'error',
        title,
        autoCloseTime: 0,
      })
    }
  }

  private async onSave() {
    const errorMessage = this.validateImages()

    if (errorMessage) {
      this.$notify({
        type: 'error',
        title: errorMessage,
      })

      return
    }

    const imageTransformsAwaitingSaving: CropperImageProp[] = []
    const emittedImageIds = new Set<number | null | undefined>()

    if (this.changedSourceImageTransforms?.size) {
      this.isLoading = true

      for (const imageTransforms of this.changedSourceImageTransforms.values()) {
        const image = imageTransforms.find((item) =>
          this.sourceNonRequired
            ? item.transform.image
            : item.transform.image?.copyrightImage?.source
        )?.transform.image

        if (!image) {
          continue
        }

        let newImage: IImage | null = null

        try {
          if (image.id) {
            if (image.copyrightImage?.source) {
              newImage = await SiriusAPI.image
                .updateImageSource({
                  id: image.id,
                  source: image.copyrightImage?.source,
                  source_url: image.copyrightImage.source_url || undefined,
                  copyright: image.copyrightImage.copyright_file || undefined,
                })
                .then(({ item }) => item)
            }
          } else if (
            image.file &&
            (image.copyrightImage?.source || this.sourceNonRequired)
          ) {
            const formData = new FormData()

            formData.append('file', image.file)

            if (image.copyrightImage?.source) {
              formData.append('source', image.copyrightImage.source)
            }

            if (image.copyrightImage?.copyright_file) {
              formData.append('copyright', image.copyrightImage.copyright_file)
              formData.append('source_url', image.copyrightImage.source_url)
            }

            newImage = await SiriusAPI.image
              .addImage({ file: formData })
              .then(({ item }) => item)
          }
        } catch (e) {
          const error = e as AxiosError<unknown>
          const errorMessage =
            typeof error?.response?.data === 'string'
              ? error.response.data
              : messages.errors.somethingWentWrong

          const cropDescriptions = this.formatCropDescriptions(
            new Set(imageTransforms.map(({ key }) => key))
          )

          this.$notify({
            title: `Ошибка в ${cropDescriptions}:<br /><br />${errorMessage}`,
            type: 'error',
            autoCloseTime: 5e3,
          })

          this.isLoading = false

          return
        }

        if (newImage) {
          imageTransforms.forEach((imageTransform) => {
            if (!newImage) {
              return
            }

            if (!newImage.copyrightImage) {
              newImage.copyrightImage = createEmptyCopyrightImage()
            }

            if (!emittedImageIds.has(newImage.id)) {
              emittedImageIds.add(newImage.id)

              this.$emit('load:image', {
                ...newImage,
                key: imageTransform.key,
              })
            }

            imageTransform.transform.image = newImage
            imageTransform.transform.image_id = newImage.id
          })
        }
      }

      this.isLoading = false
    }

    for (const localImage of this.localImages) {
      const initialImage = this.initialImages.find(
        (image) => image.key === localImage.key
      )

      if (
        initialImage &&
        !this.errorSaveRequestKeys.includes(initialImage.key) &&
        !this.hasChangesInImageTransform(initialImage, localImage)
      ) {
        continue
      }

      imageTransformsAwaitingSaving.push(localImage)
    }

    imageTransformsAwaitingSaving.forEach((imageTransform) => {
      this.saveImage(imageTransform)
    })

    this.initialImages = nestedFreeze(cloneObject(this.localImages))

    this.$emit('save:local', cloneObject(this.localImages))

    if (this.currentImageIndex === -1) {
      this.currentImageIndex = this.getDefaultImageIndex()
    }
  }

  private onReady() {
    this.isReadyCropper = true
  }

  private onChange({
    coordinates,
    image,
    visibleArea,
  }: {
    coordinates: CropperImageCoordinates
    image: CropperImage
    visibleArea?: CropperVisibleArea
  }) {
    if (
      !this.isReadyCropper ||
      !this.currentImage ||
      !visibleArea ||
      !this.cropperRef
    ) {
      return
    }

    const currentLocalImage = this.localImages[this.currentImageIndex]

    const { imageSize } = this.cropperRef

    if (this.isDynamicRatio && currentLocalImage) {
      currentLocalImage.transform.crop_h = Math.round(
        Math.min(visibleArea.height, imageSize.height)
      )

      currentLocalImage.transform.crop_w = Math.round(
        Math.min(visibleArea.width, imageSize.width)
      )

      currentLocalImage.transform.crop_x = Math.round(
        Math.max(visibleArea.left, 0)
      )

      currentLocalImage.transform.crop_y = Math.round(
        Math.max(visibleArea.top, 0)
      )

      this.currentZoomChange()
      return
    }

    const { width, height } = coordinates
    const left = Math.round(Math.max(coordinates.left, 0))
    const top = Math.round(Math.max(coordinates.top, 0))
    let imageForAllFormats: IImage | null = null

    this.currentZoomChange()

    if (this.localOneImageForAllFormats) {
      imageForAllFormats =
        this.currentImage.transform.image ||
        this.localImages.find(({ transform: { image } }) => image?.url)
          ?.transform.image ||
        null
    }

    this.localImages.forEach((localImage, imageIndex) => {
      const transformImage = imageForAllFormats || localImage.transform.image

      if (this.currentImageIndex === imageIndex) {
        localImage.transform.crop_x = left
        localImage.transform.crop_y = top
        localImage.transform.crop_w = width
        localImage.transform.crop_h = height
      } else if (
        this.currentImageIndex === -1 ||
        (this.localOneImageForAllFormats &&
          localImage.transform.image?.url !== transformImage?.url)
      ) {
        const reversed = Math.abs(image.transforms.rotate) % 180 > 0

        const imageWidth = reversed ? image.height : image.width
        const imageHeight = reversed ? image.width : image.height

        let offsetLeft = left / (imageWidth - width)
        let offsetTop = top / (imageHeight - height)

        if (Number.isNaN(offsetLeft)) {
          offsetLeft = 0.5
        }

        if (Number.isNaN(offsetTop)) {
          offsetTop = 0.5
        }

        const { crop_x, crop_y, crop_w, crop_h } = this.calcDefaultCoordinates({
          aspectRatio: localImage.aspectRatio,
          imgWidth: imageWidth,
          imgHeight: imageHeight,
          zoom: this.currentZoom,
          offsetLeft,
          offsetTop,
        })

        localImage.transform.crop_x = crop_x
        localImage.transform.crop_y = crop_y
        localImage.transform.crop_w = crop_w
        localImage.transform.crop_h = crop_h
      }

      localImage.transform.image = transformImage || null
      localImage.transform.image_id = transformImage?.id || null
    })

    this.imageSize.width = image.width
    this.imageSize.height = image.height

    this.visibleArea.width = Math.floor(visibleArea.width)
    this.visibleArea.height = Math.floor(visibleArea.height)
  }

  private currentZoomChange() {
    const cropper = this.cropperRef
    if (cropper) {
      const { coordinates, imageSize } = cropper

      if (
        imageSize.width / imageSize.height >
        coordinates.width / coordinates.height
      ) {
        this.currentZoom =
          (cropper.imageSize.height - cropper.coordinates.height) /
          (cropper.imageSize.height - cropper.sizeRestrictions.minHeight)
      } else {
        this.currentZoom =
          (cropper.imageSize.width - cropper.coordinates.width) /
          (cropper.imageSize.width - cropper.sizeRestrictions.minWidth)
      }
    }
  }

  private get controlsConfig() {
    return [
      {
        tooltip: 'Поворот против часовой',
        icon: 'mdi-rotate-left',
        action: 'rotate',
        params: { angle: -90 },
        enabled: false,
      },
      {
        tooltip: 'Поворот по часовой',
        icon: 'mdi-rotate-right',
        action: 'rotate',
        params: { angle: 90 },
        enabled: false,
      },
      {
        tooltip: 'Отразить по горизонтали',
        icon: 'mdi-swap-horizontal',
        action: 'flip',
        params: { vertical: false, horizontal: true },
        enabled: false,
      },
      {
        tooltip: 'Отразить по вертикали',
        icon: 'mdi-swap-vertical',
        action: 'flip',
        params: { vertical: true, horizontal: false },
        enabled: false,
      },
      {
        tooltip:
          this.localOneImageForAllFormats && this.currentImageIndex === -1
            ? 'Удалить все картинки'
            : 'Удалить',
        icon: 'mdi-delete-outline',
        action: 'reset',
        params: null,
      },
    ].filter(({ enabled = true }) => enabled)
  }

  private onLoadStart() {
    this.$emit('load:start')
  }

  private resetCopyrightForm() {
    this.imageSourceRef?.resetForm()
  }

  private onLoadImage({
    url,
    id,
    width,
    height,
    file,
    copyrightImage,
    ...payload
  }: SizeImage) {
    if (!this.currentImage || this.currentOriginalImageUrl === url) {
      return
    }

    this.imageSize.width = width
    this.imageSize.height = height

    this.currentZoom = 0

    if (this.currentImageIndex > -1 && !this.oneImageForAllFormats) {
      this.applyImageTransform(
        this.localImages[this.currentImageIndex],
        {
          url,
          id,
          file,
          copyrightImage,
        },
        this.calcDefaultCoordinates({
          aspectRatio: this.currentAspectRatio,
          imgWidth: width,
          imgHeight: height,
        })
      )
    } else {
      this.localImages.forEach((localImage) => {
        this.applyImageTransform(
          localImage,
          {
            url,
            id,
            file,
            copyrightImage,
          },
          this.calcDefaultCoordinates({
            aspectRatio: localImage.aspectRatio,
            imgWidth: width,
            imgHeight: height,
          })
        )
      })
    }

    this.forceRecreateCropperKey++

    if (this.enabledSource) {
      return
    }

    this.$emit('load:image', {
      ...payload,
      url,
      id,
      width,
      height,
      key: this.currentImage.key,
    })
  }

  private onZoom(value: number) {
    const oldZoom = this.currentZoom

    value = value / 100

    this.currentZoom = value

    const cropper = this.cropperRef
    if (cropper) {
      if (cropper.imageSize.height < cropper.imageSize.width) {
        const minHeight = cropper.sizeRestrictions.minHeight
        const imageHeight = cropper.imageSize.height

        cropper.zoom(
          (imageHeight - oldZoom * (imageHeight - minHeight)) /
            (imageHeight - value * (imageHeight - minHeight))
        )
      } else {
        const minWidth = cropper.sizeRestrictions.minWidth
        const imageWidth = cropper.imageSize.width
        cropper.zoom(
          (imageWidth - oldZoom * (imageWidth - minWidth)) /
            (imageWidth - value * (imageWidth - minWidth))
        )
      }
    }
  }

  private flip(params: Record<string, boolean>) {
    this.cropperRef?.flip(params.horizontal, params.vertical)
  }

  private rotate(params: Record<string, number>) {
    this.cropperRef?.rotate(params.angle)
  }

  private controlAction(
    action: string,
    params: Record<string, boolean | number> | null
  ) {
    switch (action) {
      case 'reset':
        this.onReplacePreview(this.currentImageIndex)
        break
      case 'flip':
        this.flip(params as Record<string, boolean>)
        break
      case 'rotate':
        this.rotate(params as Record<string, number>)
        break
      // default:
      //   this.move(action)
    }
  }

  private initImages() {
    if (this.isSaving) {
      return
    }

    this.initialImages = cloneObject(this.images)

    this.alwaysSelectedPreview =
      this.selectedPreview ||
      this.initialImages.some(
        ({ transform: { crop_h, crop_w, image } }) =>
          image?.id && crop_h && crop_h > 0 && crop_w && crop_w > 0
      )

    this.toggleOneImageForAllFormats(
      this.initialImages.every(
        (image) =>
          image.transform.image?.id ===
          this.initialImages[0].transform.image?.id
      )
    )

    this.localImages = this.initialImages.map((image) => {
      const transform = cloneObject(image.transform)

      return {
        ...image,
        transform,
      }
    })
  }

  private closeCropper() {
    this.$emit('close')
  }

  private created() {
    this.$watch(
      () => {
        return this.isActiveCropper
      },
      (isActiveCropper) => {
        if (isActiveCropper) {
          this.initImages()
        }
      },
      {
        immediate: true,
      }
    )

    this.$watch(
      () => {
        return [
          this.currentAspectRatio,
          this.currentImageIndex,
          this.localOneImageForAllFormats,
          this.currentImage?.transform.image?.url,
          this.forceRecreateCropperKey,
        ].join('-')
      },
      () => {
        this.isReadyCropper = false

        this.currentOriginalImageUrl =
          this.currentImage?.transform.image?.url || ''

        this.stencilAspectRatio = this.currentAspectRatio || 0
        this.cropperKey++
      },
      {
        immediate: true,
      }
    )
  }

  private onLoadFile(params: { file?: File; image?: SizeImage }) {
    if (params.file) {
      const imageUrl = URL.createObjectURL(params.file)
      const image = new Image()

      this.objectURLImages.push(imageUrl)

      image.onload = () => {
        this.onLoadImage({
          width: image.width,
          height: image.height,
          id: 0,
          url: imageUrl,
          file: params.file,
          copyrightImage: createEmptyCopyrightImage(),
        })
      }

      image.onerror = () => {
        this.$notify({
          type: 'error',
          title: 'Не удалось загрузить изображение',
        })
      }

      image.src = imageUrl
    }

    if (params.image) {
      this.onLoadImage({
        from: params.image.from,
        width: params.image.width,
        height: params.image.height,
        id: params.image.id,
        url: params.image.url,
        copyrightImage:
          params.image.copyrightImage || createEmptyCopyrightImage(),
      })

      this.forceImageSourceKey++
    }
  }

  private onUpdateImageSource(params: {
    imageUrl: string
    imageId?: number
    source?: string
    sourceUrl: string
    file?: File
    copyrightImageUrl?: string
  }) {
    this.localImages.forEach((localImage) => {
      if (!localImage.transform.image) {
        return
      }

      if (
        localImage.transform.image?.id !== params.imageId &&
        localImage.transform.image?.url !== params.imageUrl
      ) {
        return
      }

      localImage.transform.image.copyrightImage =
        localImage.transform.image.copyrightImage || createEmptyCopyrightImage()

      if (params.file !== undefined) {
        URL.revokeObjectURL(localImage.transform.image.copyrightImage.url)

        const copyrightFileUrl = params.file
          ? URL.createObjectURL(params.file)
          : null

        if (copyrightFileUrl) {
          this.objectURLImages.push(copyrightFileUrl)
        }

        Vue.set(
          localImage.transform.image.copyrightImage,
          'copyright_file',
          params.file
        )

        Vue.set(
          localImage.transform.image.copyrightImage,
          'url',
          copyrightFileUrl
        )
      }

      if (params.source !== undefined) {
        Vue.set(
          localImage.transform.image.copyrightImage,
          'source',
          params.source
        )
      }

      if (params.sourceUrl !== undefined) {
        Vue.set(
          localImage.transform.image.copyrightImage,
          'source_url',
          params.sourceUrl
        )
      }

      if (params.copyrightImageUrl !== undefined) {
        URL.revokeObjectURL(localImage.transform.image.copyrightImage.url)

        Vue.set(
          localImage.transform.image.copyrightImage,
          'url',
          params.copyrightImageUrl
        )
      }
    })
  }

  private mounted() {
    this.$watch(
      () => {
        return [
          this.value,
          this.alwaysSelectedPreview,
          this.isActiveCropper,
        ].join('-')
      },
      () => {
        if (!this.isActiveCropper) {
          return
        }

        const imageUrl = this.currentImage?.transform.image?.url || ''

        if (this.value > -1) {
          this.currentImageIndex =
            imageUrl && this.currentImageIndex > -1
              ? this.currentImageIndex
              : this.value
        } else if (this.alwaysSelectedPreview) {
          this.currentImageIndex = this.getDefaultImageIndex()
        } else {
          this.currentImageIndex =
            imageUrl && this.currentImageIndex > -1
              ? this.currentImageIndex
              : -1
        }
      },
      { immediate: true }
    )

    const saveCroppers = () => {
      this.localImages.forEach((localImage) => {
        const newImage = cloneObject(localImage)

        if (!localImage.transform.image_id) {
          newImage.transform.id = null
        }

        this.$emit('save', newImage)
      })
    }

    this.$watch(
      () => {
        return this.isSaving
      },
      (isSaving) => {
        if (!isSaving) {
          saveCroppers()

          this.$emit('save:success', this.localImages)
        }

        this.$emit('update:loading', isSaving)
        this.$emit('update:saving', isSaving)
      }
    )

    this.$watch('currentImageIndex', (newIndex: number) => {
      this.$emit('change', newIndex)
    })

    this.$emit('ready')
  }

  private beforeDestroy() {
    this.$emit('update:loading', false)
    this.$emit('update:saving', false)

    this.objectURLImages.forEach((objectURLImage) => {
      URL.revokeObjectURL(objectURLImage)
    })
  }
}
